import { reactive } from 'vue';
import { getDocumentPrefixes } from '../api/document-prefixes.api';

export interface UrlUploadInstanceData {
  id: number;
  url: string;
  prefixId?: number | string;
  prefixCode?: string;
  numbering: number;
  sequential: number;
  changelog: string;
  managerId?: number | string;
  documentName?: string;
  isValid: boolean;
}

interface DocumentPrefix {
  id: number;
  code: string;
  name: string;
}

class UrlUploadDataStore {
  private readonly instances = reactive<Map<number, UrlUploadInstanceData>>(new Map());
  private readonly prefixMap = reactive<Map<number, string>>(new Map());

  async initializePrefixes() {
    try {
      const prefixes: DocumentPrefix[] = await getDocumentPrefixes();
      prefixes.forEach((prefix) => {
        this.prefixMap.set(prefix.id, prefix.code);
      });
    } catch (error) {
      console.error('Error loading prefixes:', error);
    }
  }

  createInstance(id: number): UrlUploadInstanceData {
    const instance: UrlUploadInstanceData = {
      id,
      url: '',
      numbering: 0,
      sequential: 0,
      changelog: '',
      isValid: false,
    };
    this.instances.set(id, instance);
    return instance;
  }

  getInstance(id: number): UrlUploadInstanceData | undefined {
    return this.instances.get(id);
  }

  getOrCreateInstance(id: number): UrlUploadInstanceData {
    const existing = this.instances.get(id);
    if (existing) return existing;
    return this.createInstance(id);
  }

  updateInstance(id: number, updates: Partial<UrlUploadInstanceData>) {
    const instance = this.getInstance(id);
    if (instance) {
      Object.assign(instance, updates);
    }
  }

  removeInstance(id: number) {
    this.instances.delete(id);
  }

  getAllInstances(): UrlUploadInstanceData[] {
    return Array.from(this.instances.values());
  }

  getValidInstances(): UrlUploadInstanceData[] {
    return this.getAllInstances().filter((instance) => instance.isValid);
  }

  clearAll() {
    this.instances.clear();
  }

  getPrefixCode(prefixId: number | string): string | undefined {
    if (typeof prefixId === 'number') {
      return this.prefixMap.get(prefixId);
    }
    return prefixId;
  }

  generateFileName(instance: UrlUploadInstanceData, extension = 'pdf'): string {
    if (instance.prefixCode) {
      const numberingStr = instance.numbering.toString().padStart(2, '0');
      return `${instance.prefixCode}${numberingStr}.${instance.sequential}.${extension}`;
    }
    return '';
  }

  generateExternalFileName(documentName: string, extension = 'pdf'): string {
    return `(DE) ${documentName}.${extension}`;
  }

  getUrlExtension(url: string): string {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      const pathname = urlObj.pathname;
      const regex = /\.([a-zA-Z0-9]+)$/;
      const match = regex.exec(pathname);
      return match ? match[1].toLowerCase() : 'pdf';
    } catch {
      return 'pdf';
    }
  }
}

export const urlUploadDataStore = new UrlUploadDataStore();

